import type { Annotation } from '../../annotation/types';
import { applyAffine, invertAffine } from '../../viewer/affine';
import { relativeTime } from '../discussion/api';

/**
 * The hover over an annotation on the slide: who drew it, and when it was last
 * changed (or drawn, if never since) — opened by the pointer there, or by its
 * row hovered in the Annotations list.
 *
 * The overlay asks for it by where the pointer is, not by pointer events on the
 * shapes: somebody else's annotation takes no pointer, so a press on it pans
 * the slide (AnnotationOverlay `locked`), and it is still to be hovered.
 */

/**
 * The annotation drawn under a point on screen, topmost first, or null.
 *
 * Asks the shapes as drawn — each part tagged `data-hover` with its
 * annotation's id: a line's hit stroke, a region's fill, a counter's dots, a
 * label — whether the point is in them. Their pointer-events play no part, and
 * the layer's transform while it catches up with the map is in the matrix.
 */
export function annotationAt(host: Element, clientX: number, clientY: number): string | null {
  const parts = host.querySelectorAll<SVGGeometryElement>('[data-hover]');
  for (let i = parts.length - 1; i >= 0; i--) {
    const el = parts[i];
    const m = el.getScreenCTM?.();
    if (!m) continue;
    // Only its six numbers are read: Chromium's screen CTM is an SVGMatrix,
    // Firefox's a DOMMatrix, and the two share no transformPoint. A matrix
    // with no inverse (a layer painted with no area) puts nothing under the pointer.
    const back = invertAffine([m.a, m.b, m.c, m.d, m.e, m.f]);
    if (!back) continue;
    const p = applyAffine(back, clientX, clientY);
    if (el.isPointInFill(p) || el.isPointInStroke(p)) return el.getAttribute('data-hover');
  }
  return null;
}

/** "Just now" and "Yesterday" begin a sentence in the comments; here they follow a verb. */
const after = (iso: string) => {
  const t = relativeTime(iso);
  return t === 'Just now' || t === 'Yesterday' ? t.toLowerCase() : t;
};

/**
 * The two lines of the hover: who drew it ("Someone" when that is not known),
 * then "Created …" or, once changed since, "Edited …" — naming who edited it
 * when that was somebody else, as it could be before an annotation was only
 * its author's to change.
 */
export function annotationByline(a: Annotation): { author: string; when: string } {
  const author = a.createdByName || 'Someone';
  const edited = Date.parse(a.updatedAt) > Date.parse(a.createdAt);
  if (!edited) return { author, when: `Created ${after(a.createdAt)}` };
  const by = a.modifiedBy && a.modifiedBy !== a.createdBy ? ` by ${a.modifiedByName || 'someone'}` : '';
  return { author, when: `Edited${by} ${after(a.updatedAt)}` };
}

/** The hover's two lines, as its tooltip shows them. */
export function AnnotationByline({ annotation }: { annotation: Annotation }) {
  const { author, when } = annotationByline(annotation);
  return (
    <>
      <div className="font-medium">{author}</div>
      <div className="opacity-80">{when}</div>
    </>
  );
}
